/**
 * Decides when to ask the shopper the two feedback questions.
 *
 * Asking the moment the tour ends collects opinions about books nobody has
 * looked at yet, so the prompt waits for two things at once: enough time on the
 * page to have read some of it, and a sign that the shopper is genuinely
 * browsing rather than sitting on the fold. Whichever lands last opens the
 * modal, after a short pause so it never appears mid-tap.
 *
 * A shopper who never engages is never interrupted — the feedback tab stays
 * available for them to volunteer instead.
 */

import { useCallback, useEffect, useRef } from 'react';

/** Time on the page before the prompt is allowed to appear at all. */
const MIN_DWELL_MS = 45_000;
/** Quiet gap between the qualifying interaction and the modal. */
const SETTLE_MS = 1_500;
/** Scrolling this far past where the tour left off counts as browsing. */
const SCROLL_DEPTH_PX = 600;

interface FeedbackPromptOptions {
  /** Starts the clock. Stays false when the tour was skipped or never ran. */
  armed: boolean;
  onPrompt: () => void;
}

/**
 * @returns a callback to report a deliberate interaction (shelving a book,
 * opening one) that should count as browsing.
 */
export const useFeedbackPrompt = ({ armed, onPrompt }: FeedbackPromptOptions): (() => void) => {
  const hasBrowsed = useRef(false);
  const hasDwelled = useRef(false);
  const hasPrompted = useRef(false);
  const settleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onPromptRef = useRef(onPrompt);

  useEffect(() => {
    onPromptRef.current = onPrompt;
  }, [onPrompt]);

  const promptWhenReady = useCallback(() => {
    if (hasPrompted.current || !hasBrowsed.current || !hasDwelled.current) return;
    hasPrompted.current = true;
    settleTimer.current = setTimeout(() => onPromptRef.current(), SETTLE_MS);
  }, []);

  const noteBrowsing = useCallback(() => {
    hasBrowsed.current = true;
    promptWhenReady();
  }, [promptWhenReady]);

  useEffect(() => {
    if (!armed) return;

    const dwellTimer = setTimeout(() => {
      hasDwelled.current = true;
      promptWhenReady();
    }, MIN_DWELL_MS);

    // Measured from where the tour left the page, so the scrolling it did on
    // the shopper's behalf does not count as the shopper browsing.
    const origin = window.scrollY;
    const handleScroll = () => {
      if (Math.abs(window.scrollY - origin) > SCROLL_DEPTH_PX) noteBrowsing();
    };
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      clearTimeout(dwellTimer);
      window.removeEventListener('scroll', handleScroll);
      // Disarming mid-countdown (the shopper opened feedback themselves) drops
      // the pending ask rather than letting it surface behind their own form.
      if (settleTimer.current) clearTimeout(settleTimer.current);
    };
  }, [armed, promptWhenReady, noteBrowsing]);

  return noteBrowsing;
};
